import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { IMAGES } from "@/data/site";
import { LUX_EASE } from "@/components/Reveal";

function MaskedLine({ children, delay, className = "" }) {
  return (
    <span className="block overflow-hidden pb-2">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: LUX_EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      className="grain relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-[1.18]">
        <motion.img
          src={IMAGES.hero}
          alt="Misty tea plantation hills of India"
          initial={{ scale: 1.12, opacity: 0.4 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.8, ease: LUX_EASE }}
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/70 to-obsidian/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/60" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(11,12,14,0.75)_100%)]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative mx-auto w-full max-w-7xl px-6 pb-28 pt-32 lg:px-10"
      >
        <motion.p
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.25, ease: LUX_EASE }}
          className="inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-luxe text-gold sm:text-xs"
        >
          <span className="h-px w-10 bg-gold/70" aria-hidden="true" />
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="text-shadow-lux mt-7 font-serif text-[13vw] font-medium leading-[1.04] text-cream sm:text-6xl md:text-7xl lg:text-8xl">
          <MaskedLine delay={0.4}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.55} className="gold-text gold-sheen italic">
            {t.hero.line2}
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: LUX_EASE }}
          className="mt-7 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: LUX_EASE }}
          className="mt-11 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/our-tea"
            data-testid="hero-explore-tea-button"
            className="bg-gold px-8 py-4 text-sm font-medium uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_40px_rgba(212,175,55,0.35)]"
          >
            {t.hero.ctaPrimary}
          </Link>
          <Link
            to="/about"
            data-testid="hero-discover-button"
            className="border border-cream/30 px-8 py-4 text-sm font-medium uppercase tracking-[0.15em] text-cream transition-all duration-300 hover:border-gold hover:text-gold"
          >
            {t.hero.ctaSecondary}
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-0 left-0 right-0 hidden items-end justify-between px-6 pb-9 lg:px-10 md:flex"
      >
        <p className="text-[11px] uppercase tracking-luxe text-cream/50">
          {t.hero.est} <span className="mx-2 text-gold/60">·</span> {t.hero.place}
        </p>
        <div className="flex flex-col items-center gap-3">
          <span className="text-[10px] uppercase tracking-luxe text-cream/50">
            {t.hero.scroll}
          </span>
          <span className="h-12 w-px animate-scroll-line bg-gold/70" aria-hidden="true" />
        </div>
      </motion.div>
    </section>
  );
}
